import { MutableRefObject } from "react";

import { parseSubtitlesItem } from "../components/player-ui/helpers";
import { PlayerStoreProperties } from "../store/store";
import { PlayerRefreshEvent } from "../types/common";
import { ScheduledProgram } from "../types/player-api";

import { PauseableTimeout, setPauseableTimeout } from "./pauseableTimeout";

const minimumSetTimeoutDelay = 10000;

const debugLogging = ({
  currentServiceSchedule,
  nextServiceSchedule,
  targetTime
}: {
  currentServiceSchedule: ScheduledProgram | undefined;
  nextServiceSchedule: ScheduledProgram | undefined;
  targetTime: number | null;
}): void => {
  if (targetTime) {
    // eslint-disable-next-line no-console
    console.log(
      "Resolving current & next serviceSchedule",
      new Date(targetTime).toLocaleString("fi-FI", {
        timeZone: "Europe/Helsinki"
      })
    );
  }

  // eslint-disable-next-line no-console
  console.log("CURRENT", currentServiceSchedule);
  // eslint-disable-next-line no-console
  console.log("NEXT   ", nextServiceSchedule);

  if (nextServiceSchedule && targetTime) {
    const msToNext = Math.max(
      Date.parse(nextServiceSchedule.startTime) - targetTime,
      minimumSetTimeoutDelay
    );
    // eslint-disable-next-line no-console
    console.log(
      `%cSetting update() timeout to ${Math.trunc(
        msToNext / 1000 / 60
      )}min ${Math.trunc((msToNext / 1000) % 60)}s from now (${new Date(
        Date.now() + msToNext
      ).toLocaleTimeString("fi-FI")}).`,
      "background:#608;color:#fff"
    );
  }
};

const findCurrentServiceSchedule = (
  serviceSchedule: ScheduledProgram[],
  targetTime: number | null
): ScheduledProgram | undefined =>
  targetTime
    ? serviceSchedule.find(
        s =>
          Date.parse(s.endTime) > targetTime &&
          Date.parse(s.startTime) <= targetTime
      )
    : serviceSchedule[0];

export const resolveServiceScheduleFn = ({
  isFakeOnDemand,
  isSimulcast,
  paused,
  programId,
  refresh,
  serviceSchedule,
  serviceScheduleTimer,
  setAudioTracks,
  setScheduledProgram,
  setSubtitles,
  targetTime
}: {
  isFakeOnDemand: boolean;
  isSimulcast: boolean;
  paused: boolean;
  programId: PlayerStoreProperties["programId"];
  refresh: PlayerRefreshEvent;
  serviceSchedule: ScheduledProgram[] | undefined;
  serviceScheduleTimer: MutableRefObject<PauseableTimeout | undefined>;
  setAudioTracks: PlayerStoreProperties["setAudioTracks"];
  setScheduledProgram: PlayerStoreProperties["setScheduledProgram"];
  setSubtitles: PlayerStoreProperties["setSubtitles"];
  targetTime: number | null;
}): void => {
  serviceScheduleTimer.current?.clear();
  serviceScheduleTimer.current = undefined;

  if (programId && (isSimulcast || isFakeOnDemand) && serviceSchedule) {
    const currentServiceSchedule = findCurrentServiceSchedule(
      serviceSchedule,
      targetTime
    );

    if (currentServiceSchedule) {
      setAudioTracks(
        currentServiceSchedule.audioTracks.map(a => ({
          id: a.trackIndex,
          label: a.label,
          language: a.language,
          type: a.type
        }))
      );
      setSubtitles(
        currentServiceSchedule.subtitles.map(s => parseSubtitlesItem(s))
      );
      setScheduledProgram({
        endTime: currentServiceSchedule.endTime,
        startTime: currentServiceSchedule.startTime
      });
    }

    const nextServiceSchedule = targetTime
      ? serviceSchedule.find(s => Date.parse(s.startTime) > targetTime)
      : undefined;

    if (nextServiceSchedule && targetTime) {
      serviceScheduleTimer.current = setPauseableTimeout(
        () => {
          void refresh();
        },
        Math.max(
          Date.parse(nextServiceSchedule.startTime) - targetTime,
          minimumSetTimeoutDelay
        ),
        !paused
      );
    }

    if (__DEBUG) {
      debugLogging({
        currentServiceSchedule,
        nextServiceSchedule,
        targetTime
      });
    }
  }
};
